import React from 'react';
import { Box, Typography } from '@mui/material';
import { ArrowForward, Groups, Inventory2, ReceiptLong, TaskAlt } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ATTENTION } from './missionData';
import { s, cardSx, severity as sevMap, focusRing, reduceMotion } from './missionTokens';

const ICONS: Record<string, React.ElementType> = {
  'ar-overdue': ReceiptLong,
  'stock-risk': Inventory2,
  'po-approvals': TaskAlt,
  attrition: Groups,
};

/**
 * "What needs your attention" — the decision queue.
 *
 * Every row carries severity, the quantified impact, and exactly one action,
 * because an alert a user cannot act on from where they are standing is just
 * an anxiety generator. Sorted by severity so the list never buries the worst
 * item under the noisiest one.
 */
const AttentionPanel: React.FC = () => {
  const navigate = useNavigate();
  const order = { High: 0, Medium: 1, Low: 2 };
  const items = [...ATTENTION].sort((a, b) => order[a.severity] - order[b.severity]);

  return (
    <Box sx={{ ...cardSx, p: 2.25, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.75 }}>
        <Typography sx={{ fontSize: '0.98rem', fontWeight: 800, color: s.heading }}>
          What Needs Your Attention
        </Typography>
        <Box
          sx={{
            minWidth: 20, height: 20, px: 0.5, borderRadius: '10px', display: 'grid', placeItems: 'center',
            backgroundColor: s.danger,
          }}
        >
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: '#fff' }}>{items.length}</Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Box
          component="button"
          onClick={() => navigate('/approvals')}
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5, border: 0, background: 'none',
            cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, color: s.primary, borderRadius: 1,
            '& .arw': { transition: 'transform .25s ease' },
            '&:hover .arw': { transform: 'translateX(4px)' },
            ...focusRing, ...reduceMotion,
          }}
        >
          View All
          <ArrowForward className="arw" sx={{ fontSize: 15 }} />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {items.map((item, i) => {
          const [fg, bg] = sevMap[item.severity];
          const Icon = ICONS[item.id] ?? TaskAlt;

          return (
            <Box
              key={item.id}
              component="button"
              onClick={() => navigate(item.href)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                width: '100%',
                textAlign: 'left',
                px: 1.25,
                py: 1.15,
                cursor: 'pointer',
                borderRadius: 2,
                border: `1px solid ${s.border}`,
                backgroundColor: s.paper,
                animation: `attnIn .45s cubic-bezier(.2,.7,.2,1) ${i * 70}ms both`,
                '@keyframes attnIn': {
                  from: { opacity: 0, transform: 'translateX(8px)' },
                  to: { opacity: 1, transform: 'none' },
                },
                transition: 'border-color .22s ease, box-shadow .22s ease, transform .22s ease',
                '&:hover': { borderColor: fg, boxShadow: s.shadowSm, transform: 'translateX(2px)' },
                '&:hover .attn-go': { opacity: 1, transform: 'translateX(0)' },
                ...focusRing,
                ...reduceMotion,
              }}
            >
              <Box
                sx={{
                  width: 32, height: 32, borderRadius: 1.5, flexShrink: 0,
                  display: 'grid', placeItems: 'center', backgroundColor: bg,
                }}
              >
                <Icon sx={{ fontSize: 16, color: fg }} />
              </Box>

              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: s.heading, lineHeight: 1.35 }}>
                  {item.title}
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', color: s.muted, lineHeight: 1.35 }}>
                  {item.detail}
                </Typography>
              </Box>

              <Box
                sx={{
                  px: 0.85, py: 0.25, borderRadius: 3, flexShrink: 0,
                  backgroundColor: bg,
                }}
              >
                <Typography sx={{ fontSize: '0.64rem', fontWeight: 800, color: fg }}>
                  {item.severity}
                </Typography>
              </Box>

              <ArrowForward
                className="attn-go"
                sx={{
                  fontSize: 15, color: s.primary, flexShrink: 0,
                  opacity: 0, transform: 'translateX(-4px)',
                  transition: 'opacity .22s ease, transform .22s ease',
                  ...reduceMotion,
                }}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default AttentionPanel;
